import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { ModalFooter } from 'syf-component-library/ui/patterns/SlideInModal';
import { FooterContainer } from 'syf-component-library/ui/patterns/SlideInModal/subcomponents';
import DataTable from 'syf-component-library/ui/templates/DataTable';
import { formatCurrency, formatDisplayDate } from 'syf-js-utilities/formats';
import formatDisplayAccount from 'syf-js-utilities/formats/formatDisplayAccount';
import { DATA_ANALYTICS, DATA_OBJECT, DATA_TYPE, PAGE_FUNCTION, PAGE_NAME, SUB_FUNCTION } from '../../../../const/analytics';
import { paymentOptions } from '../../../../const/paymentOptions';
import MainContext from '../../../../Context';
import { colors } from '../../../../defaultTheme';
import { getManageAutopayMessage, isAutopayModifyEligible } from '../../../../helpers';
import { deserializePaymentOption } from '../../../../helpers/generateAutopayPayload';
import getDCHMessageType from '../../../../helpers/getDCHMessageType';
import getPaymentAmountFromOption from '../../../../helpers/getPaymentAmountFromOption';
import useApiMachine from '../../../../hooks/machines/useApiMachine';
import usePaymentFlowMachine from '../../../../hooks/machines/usePaymentFlowMachine';
import { t } from '../../../../strings/i18n';
import ManageAutopayMessage from '../../../../ui/atoms/ManageAutopayMessage';
import { PaymentModalWrapper } from '../../../../ui/atoms/styled';
import { EditAutopay } from '../subcomponents';
const DataTableManageAutopay = styled(DataTable) `
  border-radius: 0px;
  border: 0px;
  background-color: ${colors.white};

  th {
    border-bottom: 1px solid ${colors.mediumGrey};

    // visually hide the last table column's header so screen readers can still see it
    :last-of-type {
      div {
        display: none;
      }
    }
  }

  li {
    :last-of-type {
      & > div {
        &:first-child {
          display: none;
        }
      }
    }
  }
`;
const getAutopayTableHeader = (hasNextPaymentDate) => [
    hasNextPaymentDate && t('table.nextPaymentDate'),
    t('table.amount'),
    t('table.description'),
    t('table.actions')
].filter(Boolean);
const getAutopayTableData = (autopayEnrollmentDetails, paymentMethods, isEditButtonVisible, navigateToAutopayForm) => {
    const hasNextPaymentDate = Boolean(autopayEnrollmentDetails.scheduledDate);
    const paymentMethod = paymentMethods.find(({ id }) => {
        return autopayEnrollmentDetails.paymentMethodId === id;
    });
    const paymentMethodToDisplay = paymentMethod
        ? formatDisplayAccount(paymentMethod)
        : '';
    return [
        hasNextPaymentDate &&
            formatDisplayDate(autopayEnrollmentDetails.scheduledDate),
        formatCurrency(autopayEnrollmentDetails.paymentAmount),
        paymentMethodToDisplay,
        {
            value: isEditButtonVisible && (_jsx(EditAutopay, { "data-analytics": DATA_ANALYTICS.CLICK_TRACKING_EVENT, "data-object": "button", "data-reason": "edit", "data-type": DATA_TYPE.MAKE_A_PAYMENT, onClick: navigateToAutopayForm }))
        }
    ].filter(Boolean);
};
export const mapTableData = (autopayEnrollmentDetails, paymentMethods, isEditButtonVisible, navigateToAutopayForm) => {
    return [
        getAutopayTableHeader(Boolean(autopayEnrollmentDetails.scheduledDate)),
        getAutopayTableData(autopayEnrollmentDetails, paymentMethods, isEditButtonVisible, navigateToAutopayForm)
    ];
};
const ManageAutopay = () => {
    const { helpNumber, triggerAnalyticsEvent, sendToMainMachine, featureFlags } = useContext(MainContext);
    const [apiMachine] = useApiMachine();
    const { accountSummaryQueryResult, autopayStatusQueryResult, paymentMethodsQueryResult, paymentEnterpriseEligibilityQueryResult } = apiMachine.context;
    const { data: accountSummary } = accountSummaryQueryResult;
    const { data: autopayStatus } = autopayStatusQueryResult;
    const { data: paymentMethods } = paymentMethodsQueryResult;
    const { data: paymentEnterpriseEligibility } = paymentEnterpriseEligibilityQueryResult;
    const { enrollmentDetails, paymentMethod: autopayPaymentMethodACH, paymentCardInfo: autopayPaymentMethodCard } = autopayStatus;
    const nextDueDate = enrollmentDetails === null || enrollmentDetails === void 0 ? void 0 : enrollmentDetails.nextDueDate;
    const productType = accountSummary.product.type;
    const getAutopayEnrollmentDetails = () => {
        const paymentOption = deserializePaymentOption(enrollmentDetails === null || enrollmentDetails === void 0 ? void 0 : enrollmentDetails.paymentOption, accountSummary);
        const paymentAmount = paymentOption === paymentOptions.OTHER_PAYMENT
            ? Number(enrollmentDetails === null || enrollmentDetails === void 0 ? void 0 : enrollmentDetails.paymentAmount)
            : Number(getPaymentAmountFromOption(paymentOption, apiMachine.context));
        return {
            scheduledDate: enrollmentDetails === null || enrollmentDetails === void 0 ? void 0 : enrollmentDetails.nextDueDate,
            paymentMethodId: (autopayPaymentMethodACH === null || autopayPaymentMethodACH === void 0 ? void 0 : autopayPaymentMethodACH.id) || (autopayPaymentMethodCard === null || autopayPaymentMethodCard === void 0 ? void 0 : autopayPaymentMethodCard.id),
            paymentAmount
        };
    };
    const [, , sendToAutopay] = usePaymentFlowMachine();
    const navigateToAutopayForm = () => {
        sendToAutopay({ type: 'EDIT' });
    };
    const isModifyEligible = isAutopayModifyEligible(paymentEnterpriseEligibility);
    const autopayMessage = getManageAutopayMessage({
        paymentEnterpriseEligibility,
        nextDueDate,
        helpNumber,
        productType
    });
    const handleCancel = () => {
        // TODO: move this logic to autopay machine
        sendToMainMachine({
            type: 'SET_ONE_TIME_PAYMENT_FLOW'
        });
    };
    const handlePrimaryButton = () => sendToAutopay({ type: 'CANCEL' });
    useEffect(() => {
        const messageTypeDCH = getDCHMessageType(featureFlags.isDigitalCollectionsEnabled, accountSummary);
        const eventOptionsV1 = Object.assign(Object.assign({ pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.AUTOPAY, pageSubFunction: SUB_FUNCTION.AUTOPAY.MANAGE_AUTOPAY }, (messageTypeDCH && { messageType: messageTypeDCH })), { pageName: PAGE_NAME.VIEW });
        triggerAnalyticsEvent(eventOptionsV1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (_jsxs(_Fragment, { children: [_jsxs(PaymentModalWrapper, Object.assign({ all: "medium", hideOnBreakpointUp: "medium" }, { children: [(enrollmentDetails === null || enrollmentDetails === void 0 ? void 0 : enrollmentDetails.paymentOption) && (_jsx(DataTableManageAutopay, { breakpoint: 768, tableData: mapTableData(getAutopayEnrollmentDetails(), paymentMethods.paymentMethods, isModifyEligible, navigateToAutopayForm) })), autopayMessage && _jsx(ManageAutopayMessage, { message: autopayMessage })] })), _jsx(FooterContainer, { children: _jsx(ModalFooter, { dataPrimaryObject: DATA_OBJECT.BUTTON, dataPrimaryReason: "manage autopay:close", dataSecondaryObject: DATA_OBJECT.BUTTON, dataSecondaryReason: "cancel", dataType: DATA_TYPE.MAKE_A_PAYMENT, handlePrimaryButton: handlePrimaryButton, handleSecondaryButton: handleCancel, primaryText: t('button.close'), secondaryButtonType: "ghost", secondaryText: t('button.cancel') }) })] }));
};
export default ManageAutopay;
